import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Legend, ReferenceLine,
} from 'recharts';
import {
  TrendingUp, TrendingDown, CheckCircle2, XCircle,
  Trophy, ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';

// ─── Variant Comparison Table ───
export function VariantComparisonTable({ test }) {
  const { t } = useTranslation();
  const { stats } = test;

  const rows = [
    {
      label: t('ab_testing.impressions'),
      a: test.impressions.a.toLocaleString(),
      b: test.impressions.b.toLocaleString(),
    },
    {
      label: t('ab_testing.conversions'),
      a: test.conversions.a.toLocaleString(),
      b: test.conversions.b.toLocaleString(),
    },
    {
      label: t('ab_testing.conversion_rate'),
      a: `${stats.rateA}%`,
      b: `${stats.rateB}%`,
      highlight: true,
    },
    {
      label: t('ab_testing.lift'),
      a: '—',
      b: `${stats.lift > 0 ? '+' : ''}${stats.lift}%`,
      color: stats.lift > 0 ? 'text-emerald-400' : stats.lift < 0 ? 'text-red-400' : 'text-zinc-400',
    },
  ];

  return (
    <div className='border border-zinc-800 bg-zinc-950/30 overflow-hidden'>
      <div className='px-4 py-3 border-b border-zinc-800'>
        <h3 className='font-display text-sm uppercase'>{t('ab_testing.variant_comparison')}</h3>
      </div>
      <table className='w-full'>
        <thead>
          <tr className='border-b border-zinc-800'>
            <th className='text-left font-mono text-[10px] uppercase tracking-wider text-zinc-500 px-4 py-2'>
              {t('ab_testing.metric')}
            </th>
            <th className='text-right font-mono text-[10px] uppercase tracking-wider text-amber-400 px-4 py-2'>
              <div className='flex items-center justify-end gap-1.5'>
                <div className='w-2 h-2 bg-amber-500 rounded-sm' />
                {t('ab_testing.variant_a')} — {test.variantA.name}
              </div>
            </th>
            <th className='text-right font-mono text-[10px] uppercase tracking-wider text-sky-400 px-4 py-2'>
              <div className='flex items-center justify-end gap-1.5'>
                <div className='w-2 h-2 bg-sky-500 rounded-sm' />
                {t('ab_testing.variant_b')} — {test.variantB.name}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className='border-b border-zinc-800/50 last:border-b-0'>
              <td className='font-mono text-[10px] uppercase tracking-wider text-zinc-500 px-4 py-3'>
                {row.label}
              </td>
              <td className={`text-right font-mono text-sm px-4 py-3 ${row.highlight ? 'text-amber-400 font-bold' : 'text-zinc-300'}`}>
                {row.a}
              </td>
              <td className={`text-right font-mono text-sm px-4 py-3 ${row.color || (row.highlight ? 'text-sky-400 font-bold' : 'text-zinc-300')}`}>
                {row.b}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Time Series Line Chart ───
export function TimeSeriesChart({ test }) {
  const { t } = useTranslation();

  if (!test.timeSeriesA || test.timeSeriesA.length === 0) {
    return (
      <div className='border border-zinc-800 bg-zinc-950/30 p-8 text-center'>
        <div className='font-mono text-[10px] uppercase tracking-wider text-zinc-600'>
          {t('ab_testing.no_data_yet')}
        </div>
      </div>
    );
  }

  const chartData = test.timeSeriesA.map((point, i) => ({
    date: point.date,
    [test.variantA.name]: point.conversions,
    [test.variantB.name]: test.timeSeriesB[i]?.conversions || 0,
  }));

  return (
    <div className='border border-zinc-800 bg-zinc-950/30 p-5'>
      <h3 className='font-display text-sm uppercase mb-4'>{t('ab_testing.conversions_over_time')}</h3>
      <ResponsiveContainer width='100%' height={250}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray='3 3' stroke='#27272a' />
          <XAxis
            dataKey='date'
            tick={{ fill: '#71717a', fontSize: 10, fontFamily: 'monospace' }}
            tickFormatter={v => v?.slice(5) || ''}
          />
          <YAxis
            tick={{ fill: '#71717a', fontSize: 10, fontFamily: 'monospace' }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#18181b',
              border: '1px solid #3f3f46',
              borderRadius: 0,
              fontFamily: 'monospace',
              fontSize: 11,
            }}
            labelStyle={{ color: '#fbbf24' }}
          />
          <Legend
            wrapperStyle={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.1em' }}
          />
          <Line
            type='monotone'
            dataKey={test.variantA.name}
            stroke='#f59e0b'
            strokeWidth={2}
            dot={{ fill: '#f59e0b', r: 3 }}
            activeDot={{ r: 5 }}
          />
          <Line
            type='monotone'
            dataKey={test.variantB.name}
            stroke='#0ea5e9'
            strokeWidth={2}
            dot={{ fill: '#0ea5e9', r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Conversion Rate Bar Chart ───
export function ConversionRateChart({ test }) {
  const { t } = useTranslation();
  const { stats } = test;

  const data = [
    {
      name: `${t('ab_testing.variant_a')}\n${test.variantA.name}`,
      rate: stats.rateA,
      fill: '#f59e0b',
    },
    {
      name: `${t('ab_testing.variant_b')}\n${test.variantB.name}`,
      rate: stats.rateB,
      fill: '#0ea5e9',
    },
  ];

  return (
    <div className='border border-zinc-800 bg-zinc-950/30 p-5'>
      <h3 className='font-display text-sm uppercase mb-4'>{t('ab_testing.conversion_rate_comparison')}</h3>
      <ResponsiveContainer width='100%' height={200}>
        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray='3 3' stroke='#27272a' vertical={false} />
          <XAxis
            dataKey='name'
            tick={{ fill: '#71717a', fontSize: 9, fontFamily: 'monospace' }}
          />
          <YAxis
            tick={{ fill: '#71717a', fontSize: 10, fontFamily: 'monospace' }}
            tickFormatter={v => `${v}%`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#18181b',
              border: '1px solid #3f3f46',
              borderRadius: 0,
              fontFamily: 'monospace',
              fontSize: 11,
            }}
            formatter={v => [`${v}%`, t('ab_testing.conversion_rate')]}
          />
          <Bar dataKey='rate' radius={[0, 0, 0, 0]}>
            {data.map((entry, i) => (
              <rect key={i} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Winner Declaration ───
export function WinnerDeclaration({ test }) {
  const { t } = useTranslation();
  const { stats } = test;

  if (!stats.winner) {
    return (
      <div className='border border-zinc-800 bg-zinc-950/30 p-4 flex items-center gap-3'>
        <Minus className='w-5 h-5 text-zinc-500' />
        <div>
          <span className='font-mono text-xs text-zinc-400'>{t('ab_testing.no_winner_yet')}</span>
          <p className='text-zinc-600 text-xs mt-0.5'>
            {t('ab_testing.need_more_data', { confidence: stats.confidence })}
          </p>
        </div>
      </div>
    );
  }

  const isB = stats.winner === 'b';
  const winnerName = isB ? test.variantB.name : test.variantA.name;
  const winnerRate = isB ? stats.rateB : stats.rateA;
  const color = isB ? 'emerald' : 'amber';
  const Icon = isB ? TrendingUp : TrendingUp;

  return (
    <div className={`border p-4 flex items-center gap-3 ${
      isB ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'
    }`}>
      <Trophy className={`w-6 h-6 ${isB ? 'text-emerald-400' : 'text-amber-400'}`} />
      <div className='flex-1'>
        <div className={`flex items-center gap-2 ${isB ? 'text-emerald-400' : 'text-amber-400'}`}>
          <Icon className='w-4 h-4' />
          <span className='font-display text-sm uppercase'>
            {t('ab_testing.winner')}: {t('ab_testing.variant')} {stats.winner.toUpperCase()} — {winnerName}
          </span>
        </div>
        <p className='text-zinc-400 text-xs mt-1'>
          {t('ab_testing.winner_rate', { rate: winnerRate })} · {t('ab_testing.confidence')}: {stats.confidence}%
          {stats.confidence >= 95
            ? ` · ${t('ab_testing.statistically_significant')}`
            : ` · ${t('ab_testing.not_yet_significant')}`
          }
        </p>
      </div>
      {stats.confidence >= 95 && (
        <CheckCircle2 className='w-5 h-5 text-emerald-400 shrink-0' />
      )}
    </div>
  );
}

// ─── Export Results ───
export function ExportResults({ test, onExportCSV, onExportJSON, onExportPDF }) {
  const { t } = useTranslation();
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  return (
    <div className='relative'>
      <button
        onClick={() => setExportMenuOpen(!exportMenuOpen)}
        className='flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:border-zinc-600 text-zinc-400 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors'
      >
        {t('ab_testing.export_results')}
      </button>
      {exportMenuOpen && (
        <div className='absolute right-0 mt-1 w-48 bg-zinc-900 border border-zinc-800 shadow-xl z-10'>
          <button
            onClick={() => { onExportCSV?.(); setExportMenuOpen(false); }}
            className='w-full text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400 hover:bg-zinc-800 hover:text-amber-400 transition-colors'
          >
            CSV
          </button>
          <button
            onClick={() => { onExportJSON?.(); setExportMenuOpen(false); }}
            className='w-full text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400 hover:bg-zinc-800 hover:text-amber-400 transition-colors border-t border-zinc-800'
          >
            JSON
          </button>
          <button
            onClick={() => { onExportPDF?.(); setExportMenuOpen(false); }}
            className='w-full text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400 hover:bg-zinc-800 hover:text-amber-400 transition-colors border-t border-zinc-800'
          >
            PDF
          </button>
        </div>
      )}
    </div>
  );
}


